import '@js-joda/timezone';
import { DateTimeFormatter, DayOfWeek, LocalDate, LocalDateTime, LocalTime, TemporalAdjusters, ZoneId, ZoneOffset, ZonedDateTime, convert, nativeJs, } from '@js-joda/core';
import { Locale } from '@js-joda/locale_en-us';
import { formatDistanceToNowStrict } from 'date-fns';
import { isNil } from 'lodash';
import { RRule } from 'rrule';
import { match, P } from 'ts-pattern';
import { enumToCode } from './timezones.js';
export const daysOfWeek = [
    'MON',
    'TUE',
    'WED',
    'THU',
    'FRI',
    'SAT',
    'SUN',
];
export const weekdays = ['MON', 'TUE', 'WED', 'THU', 'FRI'];
export const getWeekday = (date) => {
    const weekday = date.dayOfWeek();
    if (weekday.equals(DayOfWeek.MONDAY))
        return 'MON';
    if (weekday.equals(DayOfWeek.TUESDAY))
        return 'TUE';
    if (weekday.equals(DayOfWeek.WEDNESDAY))
        return 'WED';
    if (weekday.equals(DayOfWeek.THURSDAY))
        return 'THU';
    if (weekday.equals(DayOfWeek.FRIDAY))
        return 'FRI';
    if (weekday.equals(DayOfWeek.SATURDAY))
        return 'SAT';
    if (weekday.equals(DayOfWeek.SUNDAY))
        return 'SUN';
    throw new Error(`Unexpected weekday: ${weekday.toString()}`);
};
export const isWeekend = (date) => {
    const dayOfWeek = date.dayOfWeek();
    return dayOfWeek === DayOfWeek.SATURDAY || dayOfWeek === DayOfWeek.SUNDAY;
};
export const isBeforeInclusive = (base, compareTo) => base.isBefore(compareTo) || base.equals(compareTo);
export const isAfterInclusive = (base, compareTo) => base.isAfter(compareTo) || base.equals(compareTo);
export const jsDateToLocalDate = (date) => nativeJs(new Date(date)).withZoneSameInstant(ZoneOffset.UTC).toLocalDate();
export const clinicalDocumentMinDate = () => LocalDate.now().minusYears(50);
export const clinicalDocumentMaxSelectableDate = () => LocalDate.now().with(TemporalAdjusters.lastDayOfYear());
export const clinicalDocumentMaxValidDate = () => LocalDate.now();
export const dateOfBirthMinDate = () => LocalDate.of(1900, 1, 1);
export const dateOfBirthMaxDate = () => LocalDate.now().with(TemporalAdjusters.lastDayOfYear());
export const validateDateOfBirth = (value) => {
    if (isNil(value)) {
        return { valid: true };
    }
    if (value.isAfter(LocalDate.now())) {
        return { valid: false, reason: 'Birth date must be in the past' };
    }
    if (value.isBefore(dateOfBirthMinDate())) {
        return {
            valid: false,
            reason: `Birth date must be on or after ${formatDate({
                date: dateOfBirthMinDate(),
                format: 'MMM d, yyyy',
            })}`,
        };
    }
    return { valid: true };
};
export const validateDateOfBirthForForm = (value) => {
    const result = validateDateOfBirth(value);
    if (result.valid === false) {
        return result.reason;
    }
    return true;
};
export const zoneIdFromTimezoneOrZoneId = (timezoneOrZoneId) => match(timezoneOrZoneId)
    .with({ type: 'timezone' }, ({ timezone }) => zoneId(timezone))
    .with({ type: 'zoneId' }, ({ zoneId: id }) => id)
    .exhaustive();
export const jsDateToZonedDateTime = (date, timezone) => nativeJs(new Date(date)).withZoneSameInstant(zoneId(timezone));
export const jsDateToZonedDateTimeFromTimezoneOrZoneId = (date, timezoneOrZoneId) => nativeJs(new Date(date)).withZoneSameInstant(zoneIdFromTimezoneOrZoneId(timezoneOrZoneId));
export const monthsDisplay = {
    JAN: 'January',
    FEB: 'February',
    MAR: 'March',
    APR: 'April',
    MAY: 'May',
    JUN: 'June',
    JUL: 'July',
    AUG: 'August',
    SEP: 'September',
    OCT: 'October',
    NOV: 'November',
    DEC: 'December',
};
export const months = Object.entries(monthsDisplay).map(([value, name]) => ({
    name,
    value: value,
}));
export const jsDateToLocalDateInTimezone = (date, timezone) => nativeJs(new Date(date)).withZoneSameInstant(zoneId(timezone)).toLocalDate();
/**
 * Calendar date in the practice zone → JS Date at start-of-day in that zone.
 * Pair with {@link jsDateToLocalDateInTimezone} for RRule `until` and similar.
 */
export const localDateToJsDateAtStartOfDayInTimezone = (date, timezone) => convert(date.atStartOfDay().atZone(zoneId(timezone))).toDate();
/**
 * Calculate timezone offset in minutes for a given timezone
 * @param timezone - The timezone to calculate the offset for
 * @param dateTime - Optional specific date/time to calculate offset for (defaults to current time)
 * @returns Offset in minutes from UTC (positive for ahead of UTC, negative for behind)
 */
export const getTimezoneOffsetMinutes = (timezone, dateTime) => {
    const zone = ZoneId.of(enumToCode[timezone]);
    const localTime = dateTime || LocalDateTime.now();
    const zonedDateTime = ZonedDateTime.of(localTime, zone);
    const offsetSeconds = zonedDateTime.offset().totalSeconds();
    return offsetSeconds / 60;
};
export const dateTimeFormatter = (pattern) => DateTimeFormatter.ofPattern(pattern).withLocale(Locale.US);
export const formatDate = ({ date, format = 'MM/dd/yyyy', }) => date.format(dateTimeFormatter(format));
export const zoneId = (timezone) => ZoneId.of(enumToCode[timezone]);
export const now = (timezone) => ZonedDateTime.now(zoneId(timezone));
export const getStartOfDayUtc = (timezone, dayOffset = 0) => now(timezone)
    .plusDays(dayOffset)
    .withHour(0)
    .withMinute(0)
    .withSecond(0)
    .withNano(0)
    .withZoneSameInstant(ZoneOffset.UTC);
export const nowFromTimezoneOrZoneId = (timezoneOrZoneId) => ZonedDateTime.now(zoneIdFromTimezoneOrZoneId(timezoneOrZoneId));
export const today = (timezone) => now(timezone).toLocalDate();
export const todayFromTimezoneOrZoneId = (timezoneOrZoneId) => nowFromTimezoneOrZoneId(timezoneOrZoneId).toLocalDate();
const ISO_DATE_REGEX = /([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/;
export const formatDateDisplay = (dateString) => {
    if (!dateString)
        return '';
    const matchResult = dateString.match(ISO_DATE_REGEX);
    if (!matchResult)
        return '';
    const [date] = matchResult;
    if (!date)
        return '';
    const [year, month, day] = date.split('-');
    return `${month}/${day}/${year}`;
};
export const daysBetween = (earlierDate, laterDate) => {
    const dates = [];
    let nextDate = earlierDate;
    while (!nextDate.isAfter(laterDate)) {
        dates.push(nextDate);
        nextDate = nextDate.plusDays(1);
    }
    return dates;
};
export const hoursBetween = (earlierTime, laterTime) => {
    const hours = [];
    let nextTime = earlierTime;
    while (!nextTime.isAfter(laterTime)) {
        hours.push(nextTime);
        nextTime = nextTime.plusHours(1);
    }
    return hours;
};
/**
 * getNthDayOfWeekInMonth
 *
 * Given a LocalDate determines whether the date is the 1st, 2nd, 3rd, etc of that day of the week in that month.
 * If the LocalDate is a Monday, we want to know if it's the 1st, 2nd, 3rd, etc Monday of the month.
 * This function is 1-indexed so the first Tuesday of the month will return 1 (not 0)
 */
export const getNthDayOfWeekInMonth = (date) => {
    const daysWithSameWeekday = daysBetween(date.with(TemporalAdjusters.firstDayOfMonth()), date.with(TemporalAdjusters.lastDayOfMonth())).filter((d) => d.dayOfWeek().equals(date.dayOfWeek()));
    const index = daysWithSameWeekday.findIndex((d) => d.equals(date));
    return index + 1;
};
/**
 * Parses visit-hours time strings (e.g. from forms or GraphQL). End-of-visit-day
 * midnight is serialized as "24:00"; LocalTime.parse rejects hour 24, so map to
 * LocalTime.MAX for interval logic (through end of calendar day).
 */
export const parseVisitHoursTimeString = (time) => {
    const trimmed = time.trim();
    if (trimmed === '24:00')
        return LocalTime.MAX;
    return LocalTime.parse(trimmed);
};
export const safeParseLocalTime = (str) => {
    try {
        const localTime = LocalTime.parse(str);
        return { ok: true, localTime };
    }
    catch {
        return { ok: false };
    }
};
export const safeParseLocalDate = (str) => {
    try {
        const localDate = LocalDate.parse(str);
        return { ok: true, localDate };
    }
    catch {
        return { ok: false };
    }
};
export const weekdayKeyToRRuleWeekday = (weekday) => match(weekday)
    .with('SUN', () => RRule.SU)
    .with('MON', () => RRule.MO)
    .with('TUE', () => RRule.TU)
    .with('WED', () => RRule.WE)
    .with('THU', () => RRule.TH)
    .with('FRI', () => RRule.FR)
    .with('SAT', () => RRule.SA)
    .exhaustive();
export const jsJodaWeekdayToRRuleWeekday = (weekday) => match(weekday)
    .when((d) => d.equals(DayOfWeek.SUNDAY), () => RRule.SU)
    .when((d) => d.equals(DayOfWeek.MONDAY), () => RRule.MO)
    .when((d) => d.equals(DayOfWeek.TUESDAY), () => RRule.TU)
    .when((d) => d.equals(DayOfWeek.WEDNESDAY), () => RRule.WE)
    .when((d) => d.equals(DayOfWeek.THURSDAY), () => RRule.TH)
    .when((d) => d.equals(DayOfWeek.FRIDAY), () => RRule.FR)
    .when((d) => d.equals(DayOfWeek.SATURDAY), () => RRule.SA)
    .otherwise(() => {
    throw new Error('Unexpected js-joda weekday value');
});
export const recurrenceInputToRRule = ({ startDate, input, }) => {
    const endsOn = input.ends?.on;
    return new RRule({
        dtstart: convert(startDate).toDate(),
        interval: input.repeatEveryAmount,
        count: input.ends?.afterCount,
        until: endsOn ? convert(jsDateToLocalDate(endsOn)).toDate() : undefined,
        freq: match(input.repeatEveryUnit)
            .with('DAY', () => RRule.DAILY)
            .with('WEEK', () => RRule.WEEKLY)
            .with('MONTH', () => RRule.MONTHLY)
            .with('YEAR', () => RRule.YEARLY)
            .exhaustive(),
        byweekday: match(input)
            .with({ repeatMonthlyBy: 'BY_WEEK_DAY' }, () => {
            return [
                jsJodaWeekdayToRRuleWeekday(startDate.dayOfWeek()).nth(getNthDayOfWeekInMonth(startDate)),
            ];
        })
            .with({ repeatWeeklyBy: P.nonNullable }, ({ repeatWeeklyBy }) => repeatWeeklyBy.map((weekday) => weekdayKeyToRRuleWeekday(weekday)))
            .otherwise(() => undefined),
        bymonthday: match(input.repeatMonthlyBy)
            .with('BY_MONTH_DAY', () => [startDate.dayOfMonth()])
            .with('BY_WEEK_DAY', P.nullish, () => undefined)
            .exhaustive(),
    });
};
export const buildTimestampInTimezone = ({ date, time, timezone, }) => {
    let localDate;
    try {
        localDate = jsDateToLocalDate(date);
    }
    catch {
        throw new Error('date must be a valid ISO date (YYYY-MM-DD)');
    }
    let localTime;
    try {
        localTime = LocalTime.parse(time);
    }
    catch {
        throw new Error('time must be a valid ISO time (HH:mm)');
    }
    return convert(localDate.atTime(localTime).atZone(ZoneId.of(enumToCode[timezone]))).toDate();
};
export const safeRecurrenceInputToRRule = ({ startDate, input, }) => {
    try {
        return recurrenceInputToRRule({
            startDate,
            input,
        });
    }
    catch {
        return null;
    }
};
export const formatDistanceLocale = {
    lessThanXSeconds: (c) => `${c}s`,
    xSeconds: (c) => `${c}s`,
    halfAMinute: () => '30s',
    lessThanXMinutes: (c) => `${c}m`,
    xMinutes: (c) => `${c}m`,
    aboutXHours: (c) => `${c}h`,
    xHours: (c) => `${c}h`,
    xDays: (c) => `${c}d`,
    aboutXWeeks: (c) => `${c}w`,
    xWeeks: (c) => `${c}w`,
    aboutXMonths: (c) => `${c}mo`,
    xMonths: (c) => `${c}mo`,
    aboutXYears: (c) => `${c}y`,
    xYears: (c) => `${c}y`,
    overXYears: (c) => `${c}y`,
    almostXYears: (c) => `${c}y`,
};
export const formatDistanceToNow = (dateOccurredAt, formatDistanceLocale) => formatDistanceToNowStrict(new Date(dateOccurredAt), {
    locale: {
        formatDistance: (token, count) => formatDistanceLocale[token](count),
    },
});
//# sourceMappingURL=date.js.map